import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Conversations, Presence, type Message } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useChatStore } from '@/stores/chat';
import { bindDraftsPersistence } from '@/lib/draft-persistence';
import Sidebar from '@/components/Sidebar';
import MessageList from '@/components/MessageList';
import Composer from '@/components/Composer';
import UserAvatar from '@/components/UserAvatar';
import PresenceDot from '@/components/PresenceDot';
import ThemeToggle from '@/components/ThemeToggle';
import GroupSettingsModal from '@/components/GroupSettingsModal';
import PollCreator from '@/components/PollCreator';
import ConnectionStatusBanner from '@/components/ConnectionStatusBanner';
import Modal from '@/components/Modal';
import ProfilePanel from '@/components/ProfilePanel';
import { ConversationSocket, type ServerEvent } from '@/lib/ws';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '') as string;

export default function ChatPage() {
  const { user, logout } = useAuth();
  const conversations = useChatStore((s) => s.conversations);
  const currentId = useChatStore((s) => s.currentConversationId);
  const messages = useChatStore((s) =>
    currentId ? s.messagesByConversation.get(currentId) ?? [] : [],
  );
  const members = useChatStore((s) =>
    currentId ? s.membersByConversation.get(currentId) ?? [] : [],
  );
  const setCurrent = useChatStore((s) => s.setCurrentConversation);
  const setMessages = useChatStore((s) => s.setMessages);
  const upsertMessage = useChatStore((s) => s.upsertMessage);
  const removeMessage = useChatStore((s) => s.removeMessage);
  const setMembers = useChatStore((s) => s.setMembers);
  const setTyping = useChatStore((s) => s.setTyping);
  const cleanTyping = useChatStore((s) => s.cleanTyping);
  const setPresence = useChatStore((s) => s.setPresence);
  const upsertPoll = useChatStore((s) => s.upsertPoll);
  const upsertConversation = useChatStore((s) => s.upsertConversation);
  const queueReadMarker = useChatStore((s) => s.queueReadMarker);
  const consumeReadMarker = useChatStore((s) => s.consumeReadMarker);
  // F-001: mirror WS status into the store so ConnectionStatusBanner can
  // subscribe to it. The setter is stable; we just take a ref to it here.
  const setWsStatus = useChatStore((s) => s.setWsStatus);
  // F-007: write reactions from `reaction_update` events into the store.
  const setReactions = useChatStore((s) => s.setReactions);

  const navigate = useNavigate();
  const params = useParams<{ conversationId?: string }>();

  const [composerValue, setComposerValue] = useState('');
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [showGroup, setShowGroup] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [view, setView] = useState<'sidebar' | 'chat'>('sidebar');
  const [toast, setToast] = useState<string | null>(null);
  // F-004: state-driven modals replace `window.prompt` / `window.confirm`.
  const [editing, setEditing] = useState<Message | null>(null);
  const [editValue, setEditValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Message | null>(null);
  // F-008: public profile inspector. `null` = closed.
  const [profileTarget, setProfileTarget] = useState<string | null>(null);

  const socketRef = useRef<ConversationSocket | null>(null);
  const sendTypingRef = useRef<((isTyping: boolean) => void) | null>(null);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // F-010: mirror the in-memory drafts slice to localStorage so they
  // survive a reload. Bound once on mount; the adapter seeds the store
  // from disk synchronously so the first render already sees the
  // restored drafts.
  useEffect(() => {
    return bindDraftsPersistence(useChatStore);
  }, []);

  // Expose a global toast hook so child components (PollMessage, etc.)
  // can surface transient errors without prop-drilling through the tree.
  useEffect(() => {
    (window as unknown as { __chatToast?: (m: string) => void }).__chatToast = setToast;
    return () => {
      delete (window as unknown as { __chatToast?: (m: string) => void }).__chatToast;
    };
  }, []);

  // Heartbeat presence + periodically clean typing
  useEffect(() => {
    const beat = () => Presence.heartbeat().catch(() => {});
    beat();
    const hbId = window.setInterval(beat, 60_000);
    const cleanId = window.setInterval(() => cleanTyping(), 3_000);
    return () => {
      clearInterval(hbId);
      clearInterval(cleanId);
    };
  }, [cleanTyping]);

  const current = useMemo(
    () => conversations.find((c) => c.id === currentId) ?? null,
    [conversations, currentId],
  );

  const isGroup = current?.type === 'group';
  const partnerPresence = useChatStore((s) => {
    if (!current || current.type !== 'direct') return undefined;
    const m = members.find((mm) => mm.userId !== user?.id);
    return m ? s.presenceByUser.get(m.userId) : undefined;
  });

  const fetchMessages = useCallback(
    async (cid: string) => {
      try {
        const { messages: list } = await Conversations.messages(cid, { limit: 50 });
        setMessages(cid, list, true);
        if (list.length > 0) {
          // Queue the read marker — flushed by the debounce effect, not
          // sent inline (which would re-introduce the stampede).
          const newest = list[list.length - 1];
          queueReadMarker(cid, newest.id, newest.createdAt);
        }
      } catch (err) {
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load messages';
        setToast(`Failed to load messages: ${msg}`);
      }
    },
    [setMessages, queueReadMarker],
  );

  const fetchConversation = useCallback(
    async (cid: string) => {
      try {
        const { conversation, members: m } = await Conversations.get(cid);
        upsertConversation(conversation);
        setMembers(cid, m);
        // Seed presence for direct conversations
        if (conversation.type === 'direct') {
          m.filter((mm) => mm.userId !== user?.id)
            .filter((mm) => mm.userId)
            .forEach((mm) => {
              Presence.get(mm.userId)
                .then((p) => setPresence(mm.userId, p.online, p.lastSeen))
                .catch(() => {});
            });
        }
      } catch (err) {
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load conversation';
        setToast(`Failed to load conversation: ${msg}`);
      }
    },
    [upsertConversation, setMembers, setPresence, user],
  );

  // Sync URL ↔ currentId in BOTH directions so:
  //   (a) clicking a sidebar entry pushes /c/:id and the URL stays in sync;
  //   (b) deep-linking / reloading on /c/:id opens that conversation.
  // We deliberately use `replace` for URL → state (no extra history entry when
  // the user just navigates here) and `push` for state → URL (so the back
  // button returns to the previous selection).
  useEffect(() => {
    const urlId = params.conversationId ?? null;
    if (urlId && urlId !== currentId) {
      setCurrent(urlId);
      setView('chat');
      return;
    }
    if (!urlId && currentId) {
      // The current selection lost its URL (e.g. user cleared the path).
      // Clear the selection to avoid an inconsistent state where the chat
      // is rendered for an id that isn't in the URL.
      setCurrent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.conversationId]);

  useEffect(() => {
    const urlId = params.conversationId ?? null;
    if (currentId && currentId !== urlId) {
      navigate(`/c/${encodeURIComponent(currentId)}`, { replace: false });
    }
    // NOTE: when !currentId && urlId, the OTHER effect (URL → state) is
    // about to push currentId from the URL — don't preempt it by jumping to
    // '/' or we'd break deep-linking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // When current changes, load conversation + messages + open WS
  useEffect(() => {
    if (!currentId) return;
    void fetchConversation(currentId);
    void fetchMessages(currentId);
    setView('chat');

    // Open WS
    const url = ConversationSocket.buildUrl(API_BASE || `${window.location.protocol}//${window.location.host}`, currentId);
    if (socketRef.current) {
      socketRef.current.reconnect(url);
    } else {
      const sock = new ConversationSocket({
        url,
        onEvent: (e) => handleSocketEvent(e, currentId),
        // F-001: forward WS status changes into the store. The
        // ConnectionStatusBanner reads `wsStatus` straight from the store,
        // so any consumer (including this banner) can show it without
        // holding a direct reference to the socket.
        onStatusChange: (s) => setWsStatus(s),
        onAuthError: () => {
          // Token expired mid-session — log out and route to /login.
          void logout().finally(() => navigate('/login'));
        },
      });
      socketRef.current = sock;
      sock.connect();
    }
    sendTypingRef.current = (isTyping: boolean) => {
      socketRef.current?.send({ type: 'typing', isTyping });
    };

    // Flush any pending read marker for the conversation we're leaving,
    // so unread badges update promptly when the user navigates away.
    const prevId = currentId;
    return () => {
      const pending = consumeReadMarker(prevId);
      if (pending) {
        Conversations.markRead(prevId, pending).catch(() => {});
      }
      socketRef.current?.close();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // Mark-read debounce: coalesce rapid WS messages into a single API call
  // every 1s (or immediately on visibilitychange=hidden, so we still mark
  // read before the page is closed). Without this, an active group chat
  // generates one D1 UPDATE per WS message — a stampede.
  useEffect(() => {
    const FLUSH_MS = 1_000;
    const id = window.setInterval(() => {
      if (!currentId) return;
      const pending = consumeReadMarker(currentId);
      if (!pending) return;
      Conversations.markRead(currentId, pending).catch(() => {});
    }, FLUSH_MS);

    // Flush any pending read marker using sendBeacon. Fires on tab
    // hide (desktop) AND page unload (mobile, where visibilitychange
    // sometimes never reaches 'hidden' before the process is killed).
    const flushBeacon = () => {
      if (!currentId) return;
      const pending = consumeReadMarker(currentId);
      if (!pending) return;
      let beaconed = false;
      try {
        const url = `${API_BASE}/api/conversations/${encodeURIComponent(currentId)}/read`;
        const body = JSON.stringify({ messageId: pending });
        if (navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          // sendBeacon returns false when the payload is rejected (queue
          // full, size limit, non-simple CORS). We MUST fall through to
          // the fetch path in that case — otherwise the marker is
          // silently dropped and consumeReadMarker already cleared it,
          // so there's no retry.
          beaconed = navigator.sendBeacon(url, blob);
        }
      } catch {
        beaconed = false;
      }
      if (!beaconed) {
        Conversations.markRead(currentId, pending).catch(() => {});
      }
    };
    const onVis = () => {
      if (document.visibilityState === 'hidden') flushBeacon();
    };
    const onPageHide = () => flushBeacon();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [currentId, consumeReadMarker]);

  function handleSocketEvent(e: ServerEvent, cid: string) {
    switch (e.type) {
      case 'message': {
        const msg = e.data as Message;
        if (msg && msg.conversationId === cid) {
          upsertMessage(cid, msg);
          if (msg.type === 'poll' && msg.poll) {
            upsertPoll(msg.poll);
          }
          // Queue the read marker — the debounce effect flushes it.
          // This avoids one D1 write per WS message in active chats.
          if (document.visibilityState === 'visible') {
            queueReadMarker(cid, msg.id, msg.createdAt);
          }
        }
        break;
      }
      case 'typing': {
        const t = e.data as { userId?: string; isTyping?: boolean } | undefined;
        if (t?.userId && t.userId !== user?.id) {
          setTyping(cid, t.userId, Boolean(t.isTyping));
        }
        break;
      }
      case 'presence': {
        const p = e.data as { userId?: string; online?: boolean; lastSeen?: string | number | null } | undefined;
        if (p?.userId) {
          const ls = typeof p.lastSeen === 'number' ? String(p.lastSeen) : p.lastSeen ?? null;
          setPresence(p.userId, Boolean(p.online), ls);
        }
        break;
      }
      case 'poll_update': {
        const p = e.data as { id: string; options?: { id: string; voteCount: number }[]; userVotes?: string[] };
        if (p?.id) {
          const existing = useChatStore.getState().pollsById.get(p.id);
          if (existing) {
            upsertPoll({
              ...existing,
              options: p.options?.length
                ? existing.options.map((o) => ({
                    ...o,
                    voteCount: p.options!.find((po) => po.id === o.id)?.voteCount ?? o.voteCount,
                  }))
                : existing.options,
              userVotes: p.userVotes ?? existing.userVotes,
            });
          }
        }
        break;
      }
      case 'message_edit': {
        // Remote user edited a message — patch the canonical row so the
        // view doesn't drift from the server.
        const edit = (e.data ?? {}) as { messageId?: string; conversationId?: string; message?: Message };
        if (edit.message) {
          upsertMessage(edit.message.conversationId || cid, edit.message);
        } else if (edit.messageId && edit.conversationId) {
          // Fallback: server only sent the id; mark the row as edited so
          // the user at least sees "edited" until they refetch.
          const list = useChatStore.getState().messagesByConversation.get(edit.conversationId) ?? [];
          const found = list.find((m) => m.id === edit.messageId);
          if (found) upsertMessage(edit.conversationId, { ...found, editedAt: new Date().toISOString() });
        }
        break;
      }
      case 'message_delete': {
        // Remote user deleted a message — apply soft-delete locally so
        // every connected tab stays in sync without a manual reload.
        // If the broadcast carries a server-stamped deletedAt, use it
        // so the renderer agrees with the server's clock; otherwise the
        // store falls back to the client clock (acceptable — the row
        // is hidden either way).
        const del = (e.data ?? {}) as { messageId?: string; conversationId?: string; deletedAt?: string };
        const targetCid = del.conversationId || cid;
        if (del.messageId) removeMessage(targetCid, del.messageId, del.deletedAt);
        break;
      }
      case 'member_add':
      case 'member_remove':
      case 'member_role': {
        // Member roster changed — refetch the conversation so the modal
        // and any other consumer (sidebar, presence matrix) see the new
        // roster. Cheap because we only refresh the one conversation the
        // user has open.
        const ev = (e.data ?? {}) as { conversationId?: string };
        const targetCid = ev.conversationId || cid;
        if (targetCid === cid) {
          void fetchConversation(cid);
        }
        break;
      }
      case 'reaction_update': {
        // F-007: server-broadcast reaction list. The wire payload no longer
        // carries `myReacted` (it was sender-specific and leaked to every
        // subscriber), so we merge the per-recipient `myReacted` from the
        // EXISTING store entry: if the local user already reacted with an
        // emoji, keep that flag; otherwise mark false. Counts come from
        // the broadcast.
        const r = (e.data ?? {}) as { messageId?: string; reactions?: unknown };
        if (typeof r.messageId === 'string') {
          const incoming = ((r.reactions ?? []) as Array<{ emoji: string; count: number }>).map((rr) => {
            const prior = useChatStore.getState().reactionsByMessage.get(r.messageId!) ?? [];
            const prev = prior.find((p) => p.emoji === rr.emoji);
            return { emoji: rr.emoji, count: rr.count, myReacted: Boolean(prev?.myReacted) };
          });
          setReactions(r.messageId, incoming);
        }
        break;
      }
      case 'pong':
      default:
        break;
    }
  }

  function handleSend(text: string, replyToId?: string) {
    if (!currentId) return;
    // Optimistic update via API; WS will broadcast
    Conversations.sendMessage(currentId, { content: text, replyToId })
      .then(({ message }) => {
        upsertMessage(currentId, message);
        if (message.type === 'poll' && message.poll) upsertPoll(message.poll);
        setReplyTo(null);
      })
      .catch((err) => {
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to send message';
        setToast(`Failed to send: ${msg}`);
      });
  }

  function handleEdit(msg: Message) {
    // F-004: open the modal rather than `window.prompt`. The Save button
    // calls submitEdit() below.
    setEditing(msg);
    setEditValue(msg.content);
  }

  function submitEdit() {
    if (!currentId || !editing) return;
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === editing.content) {
      setEditing(null);
      return;
    }
    fetch(`${API_BASE}/api/conversations/${encodeURIComponent(currentId)}/messages/${encodeURIComponent(editing.id)}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('cf-chat:token') ?? ''}`,
      },
      body: JSON.stringify({ content: trimmed }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(text || `edit failed (${res.status})`);
        }
        const json = (await res.json()) as { message: Message };
        upsertMessage(currentId, json.message);
        setEditing(null);
      })
      .catch((err) => {
        // Don't optimistically apply the edit on failure — that leaves
        // the client and server out of sync with no error to the user.
        const reason = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'unknown error';
        setToast(`Couldn't edit message: ${reason}`);
      });
  }

  function handleDelete(msg: Message) {
    // F-004: open the confirm modal rather than `window.confirm`.
    setConfirmDelete(msg);
  }

  function confirmDeleteNow() {
    if (!currentId || !confirmDelete) return;
    const target = confirmDelete;
    setConfirmDelete(null);
    fetch(`${API_BASE}/api/conversations/${encodeURIComponent(currentId)}/messages/${encodeURIComponent(target.id)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('cf-chat:token') ?? ''}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(text || `delete failed (${res.status})`);
        }
        removeMessage(currentId, target.id);
      })
      .catch((err) => {
        // Don't remove the message locally if the server still has it.
        const reason = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'unknown error';
        setToast(`Couldn't delete message: ${reason}`);
      });
  }

  const headerTitle = current?.name ?? (current?.type === 'direct'
    ? members.find((m) => m.userId !== user?.id)?.user?.displayName ?? 'Direct chat'
    : 'Select a chat');

  const headerAvatarSrc = current?.imageUrl ?? (current?.type === 'direct'
    ? members.find((m) => m.userId !== user?.id)?.user?.avatarUrl ?? null
    : null);

  const headerSubtitle = isGroup
    ? `${members.length} members`
    : partnerPresence?.online
      ? 'Online'
      : partnerPresence?.lastSeen
        ? `Last seen ${new Date(partnerPresence.lastSeen).toLocaleString()}`
        : '';

  return (
    <div className="flex h-full flex-col bg-ink-50 dark:bg-ink-900">
      <header className="flex items-center gap-2 border-b border-ink-100 bg-white px-3 py-2 pt-safe dark:border-ink-700 dark:bg-ink-800">
        {view === 'chat' && currentId && (
          <button
            type="button"
            className="btn-ghost px-2 md:hidden"
            aria-label="Back to chats"
            onClick={() => {
              setView('sidebar');
              navigate('/');
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <span className="font-semibold tracking-tight">CF Chat</span>
        <div className="flex-1" />
        <ThemeToggle />
        <button
          type="button"
          className="btn-ghost px-2"
          aria-label="Account menu"
          onClick={() => setShowMenu((v) => !v)}
          title="Account"
        >
          <UserAvatar name={user?.displayName || user?.username} src={user?.avatarUrl} size={28} />
        </button>
        {showMenu && (
          <div className="absolute right-3 top-12 z-30 w-48 rounded-lg border border-ink-200 bg-white p-2 shadow-soft dark:border-ink-700 dark:bg-ink-800" role="menu">
            <div className="px-2 py-1 text-xs text-ink-500 dark:text-ink-300">@{user?.username}</div>
            <button type="button" className="btn-ghost w-full justify-start" onClick={() => { setShowMenu(false); void logout(); }}>
              Sign out
            </button>
          </div>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        <div className={`min-h-0 ${view === 'chat' ? 'hidden md:flex' : 'flex'} w-full md:w-auto`}>
          <Sidebar
            activeId={currentId}
            selfUserId={user?.id ?? null}
            onSelect={(id) => {
              setView('chat');
              navigate(`/c/${encodeURIComponent(id)}`);
            }}
          />
        </div>

        <main id="main-content" className={`min-h-0 flex-1 ${view === 'chat' ? 'flex' : 'hidden'} flex-col md:flex`}>
          {current ? (
            <>
              <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-ink-100 bg-white px-3 py-2 dark:border-ink-700 dark:bg-ink-800">
                <button
                  type="button"
                  className="relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  aria-label={
                    current.type === 'direct'
                      ? `Open profile for ${headerTitle}`
                      : `Open ${headerTitle} details`
                  }
                  onClick={() => {
                    // F-008: open the partner's profile on direct chats.
                    // For group chats, we open the group-settings modal
                    // instead (the right-click / bell menu already covers
                    // per-conversation prefs).
                    if (current.type === 'direct') {
                      const partnerMember = members.find((m) => m.userId !== user?.id);
                      const username = partnerMember?.user?.username;
                      if (username) setProfileTarget(username);
                    } else {
                      setShowGroup(true);
                    }
                  }}
                >
                  <UserAvatar name={headerTitle} src={headerAvatarSrc} size={36} />
                  {!isGroup && (
                    <span className="absolute -bottom-0.5 -right-0.5">
                      <PresenceDot online={partnerPresence?.online} lastSeen={partnerPresence?.lastSeen} />
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  onClick={() => {
                    if (current.type === 'direct') {
                      const partnerMember = members.find((m) => m.userId !== user?.id);
                      const username = partnerMember?.user?.username;
                      if (username) setProfileTarget(username);
                    } else {
                      setShowGroup(true);
                    }
                  }}
                >
                  <div className="truncate font-semibold">{headerTitle}</div>
                  <div className="truncate text-xs text-ink-500 dark:text-ink-300">{headerSubtitle}</div>
                </button>
                {isGroup && (
                  <button
                    type="button"
                    className="btn-ghost px-2"
                    aria-label="Group settings"
                    onClick={() => setShowGroup(true)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </button>
                )}
              </div>

              {/* F-001: sticky connection-status banner — sits directly
                  under the header so the user can always see whether the
                  live socket is healthy. */}
              <ConnectionStatusBanner />

              <MessageList
                conversationId={current.id}
                messages={messages}
                currentUser={user}
                onReply={(m) => setReplyTo(m)}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onReplyClick={(m) => {
                  const el = document.querySelector(`[data-message-id="${m.id}"]`);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              />

              <Composer
                value={composerValue}
                onChange={setComposerValue}
                onSubmit={handleSend}
                onTyping={(b) => sendTypingRef.current?.(b)}
                replyingTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
                onCreatePoll={isGroup || current.type === 'direct' ? () => setShowPoll(true) : undefined}
                // F-010: draft persistence keyed by conversation.
                conversationId={current.id}
              />
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center text-sm text-ink-500 dark:text-ink-300" data-testid="chat-empty-state">
              <span aria-hidden="true" className="text-5xl">💬</span>
              <span className="font-medium text-ink-700 dark:text-ink-100">
                Select a conversation
              </span>
              <span className="max-w-xs text-xs text-ink-500 dark:text-ink-300">
                Choose a chat from the sidebar, or start a new one to begin messaging.
              </span>
            </div>
          )}
        </main>
      </div>

      {showGroup && current && (
        <GroupSettingsModal
          conversation={current}
          members={members}
          onClose={() => setShowGroup(false)}
        />
      )}

      {/* F-004: state-driven edit modal replacing `window.prompt`. */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Edit message"
        size="lg"
        // Focus the textarea immediately so the user can start typing
        // without tabbing past the close button.
        initialFocusSelector="textarea[data-autofocus]"
        footer={
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setEditing(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={submitEdit}
              disabled={!editValue.trim()}
            >
              Save
            </button>
          </>
        }
      >
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          rows={4}
          data-autofocus
          className="nice-scroll w-full resize-y rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-ink-700 dark:bg-ink-900"
          aria-label="Edited message"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submitEdit();
            }
          }}
        />
        <p className="mt-2 text-xs text-ink-500 dark:text-ink-300">
          Press Cmd/Ctrl + Enter to save.
        </p>
      </Modal>

      {/* F-004: state-driven delete confirm replacing `window.confirm`. */}
      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete message"
        footer={
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={confirmDeleteNow}
            >
              Delete
            </button>
          </>
        }
      >
        <p>Delete this message? This can&apos;t be undone.</p>
      </Modal>

      {/* F-008: public-profile inspector (slide-in panel). */}
      <ProfilePanel
        username={profileTarget}
        onClose={() => setProfileTarget(null)}
      />
      {showPoll && current && (
        <PollCreator
          conversationId={current.id}
          onClose={() => setShowPoll(false)}
          onCreated={(p) => {
            upsertPoll(p);
            // Reflect the poll message in the local message list so it shows
            // up immediately (the server's POST /polls returns both poll and
            // message, and we want the message to appear without a refetch).
            if (current) {
              const pollMsg: Message = {
                id: p.messageId,
                conversationId: current.id,
                senderId: user?.id ?? '',
                sender: user ? { id: user.id, username: user.username, displayName: user.displayName, avatarUrl: user.avatarUrl, lastSeen: null } : null,
                content: p.question,
                type: 'poll',
                replyToId: null,
                replyTo: null,
                poll: p,
                createdAt: new Date().toISOString(),
                editedAt: null,
                deletedAt: null,
              };
              upsertMessage(current.id, pollMsg);
            }
            setShowPoll(false);
            setToast('Poll created');
          }}
        />
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-full bg-ink-900 px-4 py-2 text-xs text-white shadow-soft dark:bg-ink-700">
          {toast}
        </div>
      )}
    </div>
  );
}
